import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { CAGService } from '../services/cagService';
import { CAGConversation, CAGResponse, ConversationStats } from '../types/cag';

interface CAGStore {
  // Current conversation state
  currentConversation: CAGConversation | null;
  conversationStats: ConversationStats | null;
  
  // UI state
  isCreatingConversation: boolean;
  isSendingMessage: boolean;
  isLoadingStats: boolean;
  error: string | null;
  
  // Message history for UI
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    sources?: string[];
    tokenUsage?: {
      prompt: number;
      completion: number;
      total: number;
    };
    optimizationStats?: {
      rulesLoaded: number;
      budgetUsed: {
        coreRules: number;
        queryRules: number;
        history: number;
      };
      historyLength: number;
    };
  }>;
  
  // Actions
  createConversation: (userId: string, gameId: string, token?: string) => Promise<void>;
  loadConversation: (sessionId: string, token?: string) => Promise<void>;
  sendMessage: (query: string, token?: string) => Promise<void>;
  loadStats: (token?: string) => Promise<void>;
  clearError: () => void;
  resetConversation: () => void;
}

const cagService = CAGService.getInstance();

export const useCAGStore = create<CAGStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        currentConversation: null,
        conversationStats: null,
        isCreatingConversation: false,
        isSendingMessage: false,
        isLoadingStats: false,
        error: null,
        messages: [],

        // Actions
        createConversation: async (userId: string, gameId: string, token?: string) => {
          try {
            set({ isCreatingConversation: true, error: null });
            
            const conversation = await cagService.createConversation(userId, gameId, token);
            
            set({
              currentConversation: conversation,
              messages: [], // Start with empty message history
              isCreatingConversation: false
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to create conversation',
              isCreatingConversation: false
            });
            throw error;
          }
        },

        loadConversation: async (sessionId: string, token?: string) => {
          try {
            set({ error: null });
            
            const conversation = await cagService.getConversation(sessionId, token);
            
            // Convert conversation history to UI messages, filtering out system messages
            const messages = conversation.messageHistory
              .filter(msg => msg.role !== 'system') // Filter out system messages first
              .map(msg => ({
                id: msg.id,
                role: msg.role as 'user' | 'assistant',
                content: msg.content,
                timestamp: msg.timestamp,
                sources: msg.rules_used ? [] : undefined, // We'll populate sources from rules if needed
              }));

            set({
              currentConversation: conversation,
              messages: messages,
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to load conversation'
            });
            throw error;
          }
        },

        sendMessage: async (query: string, token?: string) => {
          const { currentConversation } = get();
          if (!currentConversation) {
            throw new Error('No active conversation');
          }

          try {
            set({ isSendingMessage: true, error: null });

            // Add user message to UI immediately
            const userMessage = {
              id: `user_${Date.now()}`,
              role: 'user' as const,
              content: query,
              timestamp: new Date().toISOString(),
            };

            set(state => ({
              messages: [...state.messages, userMessage]
            }));

            // Send message via CAG service
            const response = await cagService.sendMessage(currentConversation.sessionId, query, token);

            // Add assistant response to UI
            const assistantMessage = {
              id: `assistant_${Date.now()}`,
              role: 'assistant' as const,
              content: response.response,
              timestamp: new Date().toISOString(),
              sources: response.sources,
              tokenUsage: response.tokenUsage,
              optimizationStats: response.optimizationStats,
            };

            set(state => ({
              messages: [...state.messages, assistantMessage],
              isSendingMessage: false
            }));

            // Update conversation state with new token usage
            set(state => ({
              currentConversation: state.currentConversation ? {
                ...state.currentConversation,
                totalTokensUsed: state.currentConversation.totalTokensUsed + response.tokenUsage.total,
                rulesLoadedCount: response.optimizationStats.rulesLoaded,
                contextOptimized: response.contextOptimized,
                lastUpdated: new Date().toISOString()
              } : null
            }));

          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to send message',
              isSendingMessage: false
            });
            throw error;
          }
        },

        loadStats: async (token?: string) => {
          const { currentConversation } = get();
          if (!currentConversation) return;

          try {
            set({ isLoadingStats: true, error: null });
            
            const stats = await cagService.getConversationStats(currentConversation.sessionId, token);
            
            set({
              conversationStats: stats,
              isLoadingStats: false
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to load stats',
              isLoadingStats: false
            });
          }
        },

        clearError: () => set({ error: null }),

        resetConversation: () => set({
          currentConversation: null,
          conversationStats: null,
          messages: [],
          error: null
        })
      }),
      {
        name: 'cag-store',
        partialize: (state) => ({
          currentConversation: state.currentConversation,
          messages: state.messages,
        }),
      }
    )
  )
);